import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import {
    Play,
    Pause,
    Maximize,
    Minimize,
    Rewind,
    FastForward,
    Sliders,
    Settings,
    VolumeOff,
    Volume2,
} from "lucide-react";
import { extractYouTubeVideoID, getSavedTime, removeSavedTime } from "../../utils/util-functions/playerUtils";
import { ReactComponent as PlayerPlay } from "../../assets/playerPlay.svg"
import { ReactComponent as PlayerPlayBg } from "../../assets/playerPlayBg.svg"
import { ReactComponent as PlayerPause } from "../../assets/playerPause.svg"
import "./OnlyPlayer.css";

const OnlyPlayer = ({ videoUrl, width, height, staticType }) => {
    const playerRef = useRef(null);
    const containerRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(!staticType ?? true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [quality, setQuality] = useState("hd720");

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLandscape, setIsLandscape] = useState(false);
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

    const [isSeeking, setIsSeeking] = useState(false);
    const [tempSeek, setTempSeek] = useState(0);

    const [showCenterControls, setShowCenterControls] = useState(false);
    const [watchNextControls, setWatchNextControls] = useState(false);
    const hideControlsTimeout = useRef(null);
    const videoId = extractYouTubeVideoID(videoUrl);

    const [showPlaybackRateMenu, setShowPlaybackRateMenu] = useState(false);
    const [showQualityMenu, setShowQualityMenu] = useState(false);

    const [showVolumeControl, setShowVolumeControl] = useState(false);
    const [currentVolume, setCurrentVolume] = useState(0);
    const [muted, setMuted] = useState(false);
    const [lastVolume, setLastVolume] = useState(100);

    const qualityMenuRef = useRef(null);
    const playbackMenuRef = useRef(null);


    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setIsPlaying(true);
                    else setIsPlaying(false);
                });
            },
            { threshold: 0.5 }
        );

        if (containerRef.current) observer.observe(containerRef.current);
        return () => {
            if (containerRef.current) observer.unobserve(containerRef.current);
        };
    }, []);

    useEffect(() => {
        const checkDevice = () => setIsMobileOrTablet(window.innerWidth <= 1024);
        checkDevice();
        window.addEventListener("resize", checkDevice);
        return () => window.removeEventListener("resize", checkDevice);
    }, []);

    const intervalRef = useRef(null);

    const togglePlayPause = () => setIsPlaying((prev) => !prev);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (qualityMenuRef.current && !qualityMenuRef.current.contains(event.target)) {
                setShowQualityMenu(false);
            }
            if (playbackMenuRef.current && !playbackMenuRef.current.contains(event.target)) {
                setShowPlaybackRateMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);


    useEffect(() => {
        if (videoId !== null) {
            const savedTime = getSavedTime(videoId);
            if (savedTime) setCurrentTime(parseFloat(savedTime));

            intervalRef.current = setInterval(() => {
                if (watchNextControls) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    removeSavedTime(videoId);
                    return;
                }
                try {
                    sessionStorage.setItem(
                        `video-${videoId}-current-time`,
                        playerRef?.current?.getCurrentTime()
                    );
                } catch (e) { }
            }, 5000);

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                try {
                    if (playerRef?.current?.getCurrentTime() !== undefined) {
                        sessionStorage.setItem(
                            `video-${videoId}-current-time`,
                            playerRef?.current?.getCurrentTime()
                        );
                    }
                } catch (e) { }
            };
        }
    }, [videoId, watchNextControls]);

    useEffect(() => {
        const handleKeyDown = (event) => {
    const tag = document.activeElement?.tagName;

    // ✅ INPUT / TEXTAREA / CONTENTEDITABLE me ho toh ignore karo
    if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        document.activeElement?.isContentEditable
    ) {
        return;
    }

    if (watchNextControls) {
        event.preventDefault();
        return;
    }

    if (event.code === "Space") {
        event.preventDefault();
        togglePlayPause();
    }

    if (event.code === "ArrowRight") {
        event.preventDefault();
        handleFastForward();
    }

    if (event.code === "ArrowLeft") {
        event.preventDefault();
        handleRewind();
    }

    if (event.code === "KeyF") {
        event.preventDefault();
        handleMaxClick();
    }

    if (event.code === "ArrowUp") {
        event.preventDefault();
        adjustVolume(true);
    }

    if (event.code === "ArrowDown") {
        event.preventDefault();
        adjustVolume(false);
    }
};
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [currentTime, duration, watchNextControls, isFullscreen, isLandscape]);

    const adjustVolume = (upKey) => {
        try {
            const internalPlayer = playerRef.current.getInternalPlayer();
            let volume = internalPlayer?.getVolume() ?? 0;
            if (upKey === true) volume = Math.min(volume + 10, 100);
            else volume = Math.max(volume - 10, 0);
            setCurrentVolume(volume);
            internalPlayer?.setVolume(volume);
            if (volume === 0) setMuted(true);
            else {
                setMuted(false);
                setLastVolume(volume);
            }
        } catch (e) { }
    };

    const handleMuteToggle = () => {
        const player = playerRef?.current?.getInternalPlayer();
        if (!player) return;

        if (muted) {
            player.unMute();
            player.setVolume(lastVolume || 50);
            setCurrentVolume(lastVolume || 50);
            setMuted(false);
        } else {
            player.mute();
            setLastVolume(currentVolume || 50);
            setCurrentVolume(0);
            setMuted(true);
        }
    };


    const handleProgress = (progress) => {
        if (duration > 0 && currentTime > 0 && duration - currentTime <= 5) {
            playerRef?.current?.seekTo(duration, "seconds");
            return;
        }
        if (!watchNextControls) setCurrentTime(progress.playedSeconds);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const handleSeekChange = (e) => {
        const newTime = parseFloat(e.target.value) * duration;
        playerRef.current.seekTo(newTime, "seconds");
        setCurrentTime(newTime);
    };

    const handlePlaybackRateChange = (rate) => {
        try {
            const internalPlayer = playerRef.current.getInternalPlayer();
            if (internalPlayer && internalPlayer.setPlaybackRate) {
                internalPlayer.setPlaybackRate(Number(rate));
                internalPlayer.setOption && internalPlayer.setOption("captions", "track", {});
            }
            setPlaybackRate(rate);
        } catch (e) { }
    };

    const handleQualityChange = (value) => {
        const selectedQuality = value;
        setQuality(selectedQuality);
        try {
            const internalPlayer = playerRef.current.getInternalPlayer();
            if (internalPlayer) {
                const videoData = internalPlayer.getVideoData();
                const videoId = videoData.video_id;
                const currentTime = internalPlayer.getCurrentTime();
                internalPlayer.loadVideoById({
                    videoId,
                    startSeconds: currentTime,
                    suggestedQuality: selectedQuality === "auto" ? "default" : selectedQuality,
                });
            }
        } catch (e) { }
    };

    const handlePlayerReady = () => {
        const internalPlayer = playerRef.current.getInternalPlayer();
        if (internalPlayer && staticType === true) internalPlayer.pauseVideo();
        if (getSavedTime(videoId) > 0) playerRef.current.seekTo(currentTime, "seconds");
        setCurrentVolume(internalPlayer?.getVolume() ?? 0);
        setPlaybackRate(1);
    };

    // Fullscreen + orientation helpers
    const tryLockOrientation = async (orientation) => {
        try {
            const screenObj = window.screen;
            if (screenObj && screenObj.orientation && screenObj.orientation.lock) {
                await screenObj.orientation.lock(orientation);
            } else if (screenObj && screenObj.lockOrientation) {
                screenObj.lockOrientation(orientation);
            } else if (screenObj && screenObj.mozLockOrientation) {
                screenObj.mozLockOrientation(orientation);
            } else if (screenObj && screenObj.msLockOrientation) {
                screenObj.msLockOrientation(orientation);
            }
        } catch (err) {
            // ignore - many browsers restrict orientation locking
        }
    };

    const enterFullscreen = async () => {
        const el = containerRef.current || playerRef.current;
        if (!el) return;
        try {
            if (el.requestFullscreen) await el.requestFullscreen();
            else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
            else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
            else if (el.msRequestFullscreen) await el.msRequestFullscreen();
            setIsFullscreen(true);
        } catch (err) {
            // ignore
        }
    };

    // New behavior: first click -> enter fullscreen (portrait). Subsequent clicks -> toggle orientation while staying fullscreen.
    const handleMaxClick = async () => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        const internalPlayer = playerRef.current?.getInternalPlayer();

        if (isIOS) {
            // 🔑 iOS ONLY: use native fullscreen
            internalPlayer?.playVideo?.();
            internalPlayer?.getIframe?.().requestFullscreen?.();
            return;
        }

        // Android / Desktop (your existing logic)
        if (!isFullscreen) {
            await enterFullscreen();
            await tryLockOrientation("portrait-primary");
            setIsLandscape(false);
            return;
        }

        const willLandscape = !isLandscape;
        await tryLockOrientation(
            willLandscape ? "landscape-primary" : "portrait-primary"
        );
        setIsLandscape(willLandscape);
    };


    // When entering fullscreen → start auto hide timer
    useEffect(() => {
        // iOS fullscreen portrait mode - force controls visibility on touch
        if (isFullscreen && isLandscape === false) {
            const handleAnyTouch = (e) => {
                setShowCenterControls(true);
                if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
                hideControlsTimeout.current = setTimeout(() => {
                    setShowCenterControls(false);
                    setShowPlaybackRateMenu(false);
                    setShowQualityMenu(false);
                    setShowVolumeControl(false);
                }, 5000);
            };

            document.addEventListener('touchstart', handleAnyTouch, { passive: true });
            return () => document.removeEventListener('touchstart', handleAnyTouch);
        }
    }, [isFullscreen, isLandscape]);

    useEffect(() => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        if (!isIOS) return;

        if (isFullscreen) {
            document.body.classList.add("ios-fullscreen-lock");
        } else {
            document.body.classList.remove("ios-fullscreen-lock");
        }

        return () => {
            document.body.classList.remove("ios-fullscreen-lock");
        };
    }, [isFullscreen]);



    // Exit fullscreen (separate action)
    const exitFullscreen = async () => {

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        if (isIOS) {
            setIsFullscreen(false);
            setIsLandscape(false);
            return;
        }
        try {
            if (document.exitFullscreen) await document.exitFullscreen();
            else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
            else if (document.mozCancelFullScreen) await document.mozCancelFullScreen();
            else if (document.msExitFullscreen) await document.msExitFullscreen();
        } catch (e) { }
    };

    useEffect(() => {
        const onFsChange = () => {
            const fsElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
            setIsFullscreen(!!fsElement);
            if (!fsElement) {
                // unlocked when user exits fullscreen
                try { if (window.screen && window.screen.orientation && window.screen.orientation.unlock) window.screen.orientation.unlock(); } catch (e) { }
                setIsLandscape(false);
            }
        };
        document.addEventListener("fullscreenchange", onFsChange);
        document.addEventListener("webkitfullscreenchange", onFsChange);
        document.addEventListener("mozfullscreenchange", onFsChange);
        document.addEventListener("msfullscreenchange", onFsChange);
        return () => {
            document.removeEventListener("fullscreenchange", onFsChange);
            document.removeEventListener("webkitfullscreenchange", onFsChange);
            document.removeEventListener("mozfullscreenchange", onFsChange);
            document.removeEventListener("msfullscreenchange", onFsChange);
        };
    }, []);

    const handleRewind = () => {
        const newTime = Math.max(currentTime - 5, 0);
        playerRef?.current?.seekTo(newTime, "seconds");
        setCurrentTime(newTime);
    };

    const handleSeekCommit = (e) => {
        const finalValue = parseFloat(e.target.value);
        const finalTime = finalValue * duration;

        playerRef.current.seekTo(finalTime, "seconds");
        setCurrentTime(finalTime);

        setIsSeeking(false);
    };


    const handleFastForward = () => {
        const newTime = Math.min(currentTime + 5, duration);
        playerRef?.current?.seekTo(newTime, "seconds");
        setCurrentTime(newTime);
    };

    const handleMouseMove = () => {
        if (isMobileOrTablet) {
            setShowCenterControls(true);
            return;
        }
        setShowCenterControls(true);
        if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
        hideControlsTimeout.current = setTimeout(() => {
            setShowQualityMenu(false);
            setShowPlaybackRateMenu(false);
            setShowVolumeControl(false);
            setShowCenterControls(false);
        }, 5000);
    };

    const handleTouchInteraction = (e) => {
        if (e) e.stopPropagation();
        setShowCenterControls(true);
        if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
        hideControlsTimeout.current = setTimeout(() => {
            setShowQualityMenu(false);
            setShowPlaybackRateMenu(false);
            setShowVolumeControl(false);
            setShowCenterControls(false);
        }, 5000);
    };

    useEffect(() => {
        if (isMobileOrTablet) setShowCenterControls(true);
    }, [isMobileOrTablet]);

    const startOver = () => {
        setWatchNextControls(false);
        setIsPlaying(true);
        togglePlayPause();
    };

    // compute container styling - when fullscreen we want full viewport
    const containerStyle = isFullscreen
        ? { width: "100vw", height: "100vh", paddingTop: 0 }
        : { width: width || "100%", height: height || "auto" };

    const containerClass = `relative ${isFullscreen ? "rounded-none" : "rounded-3xl"} ${isFullscreen ? "player-fullscreen" : "player"}`;

    return (
        <div className="md:max-w-[53rem] md:max-h-[30rem] max-w-full max-h-full">
            <div className="flex items-center gap-4 mt-3 player-wrapper">
                {videoUrl && (
                    <div
                        ref={containerRef}
                        style={containerStyle}
                        className={`fullscreen-video-container ${containerClass} ${isFullscreen ? "fullscreen" : ""
                            } ${isLandscape ? "landscape-mode" : "portrait-mode"} ${isFullscreen ? "" : "grid grid-cols-1 md:grid-cols-2 pt-[56.25%]"
                            }`}
                        onMouseMove={handleMouseMove}
                    >
                        <div className="video-rotator"><ReactPlayer
                            ref={playerRef}
                            url={videoUrl}
                            playing={isPlaying}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            playbackRate={playbackRate}
                            width="100%"
                            height="100%"
                            onProgress={({ playedSeconds }) => setCurrentTime(playedSeconds)}
                            onReady={handlePlayerReady}
                            onDuration={setDuration}
                            controls={false}
                            key={videoUrl}
                            config={{
                                youtube: {
                                    playerVars: {
                                        autoplay: 1,
                                        playsinline: 0,
                                        fs: 0,
                                        cc_load_policy: 0,
                                        rel: 0,
                                        modestbranding: 1,
                                        controls: 0,
                                    },
                                },
                            }}
                            className="absolute top-0 left-0"
                        />

                            <div>
                                {!watchNextControls && (
                                    <div
                                        className="absolute top-0 left-0 w-full h-full z-10"
                                        style={{ cursor: 'pointer', touchAction: 'manipulation' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isFullscreen) {
                                                handleTouchInteraction(e);
                                            }
                                            togglePlayPause();
                                        }}
                                        onTouchStart={(e) => {
                                            e.stopPropagation();
                                            if (isFullscreen) {
                                                handleTouchInteraction(e);
                                            }
                                        }}
                                        onTouchEnd={(e) => {
                                            e.stopPropagation();
                                            togglePlayPause();
                                        }}
                                    >
                                        {showCenterControls && (
                                            <div className="absolute inset-0 flex items-center z-20 w-full">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRewind(); }}
                                                    className="flex-1 flex justify-center items-center"
                                                    aria-label="Rewind 5 seconds"
                                                >
                                                    <div className="inline-flex items-center justify-center p-1 md:p-2 lg:p-3 bg-black bg-opacity-50 rounded-full">
                                                        <Rewind className="h-4 w-4 md:h-6 md:w-6 lg:h-8 lg:w-8 text-white" />
                                                    </div>
                                                </button>

                                                <button
                                                    onClick={(e) => { e.stopPropagation(); togglePlayPause(); }}
                                                    className="flex-1 flex justify-center items-center"
                                                    aria-label={isPlaying ? 'Pause' : 'Play'}
                                                >
                                                    <div className="inline-flex items-center justify-center p-1 md:p-2 lg:p-3 bg-black bg-opacity-50 rounded-full">
                                                        {isPlaying ? (
                                                            <div className="relative h-8 w-8 md:h-10 md:w-10 lg:h-12 lg:w-12">
                                                                <PlayerPlayBg className="h-full w-full" />
                                                                <PlayerPause className="absolute inset-0 h-3 w-3 md:h-4 md:w-4 lg:h-5 lg:w-5 m-auto text-white" />
                                                            </div>
                                                        ) : (
                                                            <div className="relative h-8 w-8 md:h-10 md:w-10 lg:h-12 lg:w-12">
                                                                <PlayerPlayBg className="h-full w-full" />
                                                                <PlayerPlay className="absolute inset-0 h-3 w-3 md:h-4 md:w-4 lg:h-5 lg:w-5 m-auto text-white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>

                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleFastForward(); }}
                                                    className="flex-1 flex justify-center items-center"
                                                    aria-label="Fast Forward 5 seconds"
                                                >
                                                    <div className="inline-flex items-center justify-center p-1 md:p-2 lg:p-3 bg-black bg-opacity-50 rounded-full">
                                                        <FastForward className="h-4 w-4 md:h-6 md:w-6 lg:h-8 lg:w-8 text-white" />
                                                    </div>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {watchNextControls && (
                                <div className="absolute top-0 left-0 w-full h-full z-50 bg-black bg-opacity-95 flex items-center justify-center cursor-pointer">
                                    <button className="bg-blue-500 text-white font-bold py-2 px-6 m-2 rounded-full hover:bg-blue-600 transition duration-300" onClick={startOver}>
                                        Start Over
                                    </button>
                                </div>
                            )}

                            {!watchNextControls && showCenterControls && (
                                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 px-2 py-1 md:px-4 md:py-2 lg:px-6 flex items-center text-white space-x-2 rounded-b-3xl z-20">
                                    <div className="relative flex items-center group" onMouseEnter={() => setShowVolumeControl(true)} onMouseLeave={() => setShowVolumeControl(false)} tabIndex={-1}>
                                        <button onClick={handleMuteToggle} className="inline-flex items-center justify-center px-2" aria-label={muted ? "Unmute" : "Mute"} tabIndex={0}>
                                            {muted || currentVolume < 1 ? (<VolumeOff className="h-4 w-4 md:h-6 md:w-6" />) : (<Volume2 className="h-4 w-4 md:h-6 md:w-6" />)}
                                        </button>
                                        {showVolumeControl && (
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex items-center z-50" style={{ height: 120, width: 40 }}>
                                                <div className="flex items-center justify-center w-full h-full bg-black bg-opacity-0 rounded-lg shadow-lg" style={{ height: 150, width: 36 }}>
                                                    <input
                                                        type="range"
                                                        min={0}
                                                        max={100}
                                                        step={1}
                                                        value={muted ? 0 : currentVolume}
                                                        onChange={(e) => {
                                                            const volume = Number(e.target.value);
                                                            const player = playerRef?.current?.getInternalPlayer();
                                                            if (!player) return;

                                                            setCurrentVolume(volume);

                                                            if (volume === 0) {
                                                                player.mute();
                                                                setMuted(true);
                                                            } else {
                                                                // 🔑 IMPORTANT PART
                                                                player.unMute();              // <-- REQUIRED
                                                                player.setVolume(volume);     // <-- THEN set volume

                                                                setMuted(false);
                                                                setLastVolume(volume);
                                                            }
                                                        }}

                                                        className="volume-vertical-slider"
                                                        style={{
                                                            width: "100px",
                                                            height: "8px",
                                                            transform: "rotate(-90deg)",
                                                            background: "#fff",
                                                            borderRadius: "8px",
                                                            outline: "none",
                                                            appearance: "none",
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step="any"
                                        value={isSeeking ? tempSeek : currentTime / duration || 0}

                                        onChange={(e) => {
                                            const v = parseFloat(e.target.value);
                                            setIsSeeking(true);
                                            setTempSeek(v);
                                        }}

                                        onMouseUp={(e) => handleSeekCommit(e)}
                                        onTouchEnd={(e) => handleSeekCommit(e)}

                                        className="w-full h-1 bg-gray-400 rounded"
                                    />


                                    <div className="text-sm w-[12rem] flex justify-center">
                                        <div>{formatTime(currentTime)} / {formatTime(duration)}</div>
                                    </div>

                                    <div className="relative" ref={playbackMenuRef}>
                                        <button onClick={() => { setShowPlaybackRateMenu((p) => !p); setShowQualityMenu(false); }} className="inline-flex items-center justify-center px-2" aria-label="Playback Speed">
                                            <Sliders className="h-4 w-4 md:h-6 md:w-6" />
                                        </button>
                                        {showPlaybackRateMenu && (
                                            <div className="absolute bottom-full mb-1 md:mb-2 right-0 bg-black bg-opacity-80 text-white rounded px-2 py-1 md:px-3 md:py-2">
                                                {[0.25, 0.5, 1, 1.25, 1.5, 2].map((rate) => (
                                                    <div key={rate} className={`px-2 py-1 cursor-pointer hover:bg-gray-700 ${playbackRate === rate ? "text-yellow-400" : ""}`} onClick={() => handlePlaybackRateChange(rate)}>{rate}x</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="relative" ref={qualityMenuRef}>
                                        <button onClick={() => { setShowQualityMenu((p) => !p); setShowPlaybackRateMenu(false); }} className="inline-flex items-center justify-center px-2" aria-label="Quality">
                                            <Settings className="h-4 w-4 md:h-6 md:w-6" />
                                        </button>
                                        {showQualityMenu && (
                                            <div className="absolute bottom-full mb-2 right-0 bg-black bg-opacity-80 text-white rounded p-2">
                                                {[
                                                    { value: "auto", label: "Auto" },
                                                    { value: "highres", label: "Highres" },
                                                    { value: "hd1080", label: "1080p" },
                                                    { value: "hd720", label: "720p" },
                                                    { value: "large", label: "480p" },
                                                    { value: "medium", label: "360p" },
                                                    { value: "small", label: "240p" },
                                                    { value: "tiny", label: "144p" },
                                                ].map((option) => (
                                                    <div key={option.value} className={`px-2 py-1 cursor-pointer hover:bg-gray-700 ${quality === option.value ? "text-yellow-400" : ""}`} onClick={() => handleQualityChange(option.value)}>{option.label}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Main Max/Orientation button - enters fullscreen (first click) and toggles orientation afterwards */}
                                    {!isFullscreen && (
                                        <button onClick={handleMaxClick} className="inline-flex items-center justify-center px-2" aria-label="Toggle Fullscreen / Orientation">
                                            <Maximize className="h-4 w-4 md:h-6 md:w-6" />
                                        </button>)}

                                    {/* Exit fullscreen button (explicit) */}
                                    {isFullscreen && (
                                        <button onClick={exitFullscreen} className="inline-flex items-center justify-center px-2" aria-label="Exit Fullscreen">
                                            <Minimize className="h-4 w-4 md:h-6 md:w-6" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OnlyPlayer;